import api from './api';
import type {
  Showcase,
  ShowcaseMedia,
  ShowcaseCreateData,
  ShowcaseUpdateData,
  ShowcaseReplyData,
  ShowcaseReplyUpdateData,
  ShowcaseReplyDeleteData,
} from '@app-types/models/showcase';

interface ShowcasesResponse {
  success: boolean;
  data: Showcase[];
  message?: string;
}

export const getShowcases = async (stylistId: string): Promise<ShowcasesResponse> => {
  try {
    const response = await api.get<ShowcasesResponse>(`/business/showcases?stylistId=${stylistId}`);
    console.log('Fetched showcases:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching showcases:', error);
    throw error;
  }
};

export const updateShowcase = async ({files, content, planIds, media, _id, serviceTypeId, postType}: ShowcaseUpdateData): Promise<Showcase> => {
  console.log('updateShowcase called with:', {
    showcaseId: _id,
    filesCount: files ? files.length : 0,
    files: files ? files.map((f: File) => ({ name: f.name, size: f.size, type: f.type })) : [],
    content,
    planIds,
    serviceTypeId,
    existingMediaCount: media ? media.length : 0,
    existingMedia: media ? media.map((m: ShowcaseMedia) => ({ key: m.key, mediaType: m.mediaType })) : []
  });

  const formData = new FormData();
  // Append all files with the same field name for multer upload.any() to work
  if (files && files.length > 0) {
    files.forEach((file: File, index: number) => {
      console.log(`Appending new file ${index + 1}: ${file.name}`);
      formData.append('files', file);
    });
  }

  formData.append('content', content);
  formData.append('planIds', planIds);
  formData.append('_id', _id);
  formData.append('media', JSON.stringify(media));
  formData.append('postType', postType || 'work');
  if (serviceTypeId) {
    formData.append('serviceTypeIds', serviceTypeId);
  }

  console.log('FormData contents:');
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      console.log(`${key}: File(${value.name}, ${value.size} bytes)`);
    } else {
      console.log(`${key}: ${value}`);
    }
  }

  try {
    console.log('Making PUT request to /business/showcase');
    const response = await api.put<Showcase>('/business/showcase', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log('Update successful, response:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating showcase:', error);
    const axiosErr = error as { response?: { data?: unknown } };
    console.error('Error response:', axiosErr.response?.data);
    throw error;
  }
};

export const deleteShowcase = async (showcaseId: string): Promise<unknown> => {
  try {
    const response = await api.delete<unknown>(`/business/showcase`, { data: { showcaseId } });
    console.log('Deleted showcase:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error deleting showcase:', error);
    throw error;
  }
};

export const createShowcase = async ({files, content, planIds, stylistId, serviceTypeId, postType}: ShowcaseCreateData): Promise<Showcase> => {
  console.log('showcaseService.createShowcase called with:', {
    filesCount: files ? files.length : 0,
    files: files ? files.map((f: File) => ({ name: f.name, size: f.size, type: f.type })) : [],
    content,
    planIds,
    stylistId,
    serviceTypeId
  });

  const formData = new FormData();
  // Append all files with the same field name for multer upload.any() to work
  files.forEach((file: File, index: number) => {
    console.log(`Appending file ${index + 1}: ${file.name}`);
    formData.append('files', file);
  });
  formData.append('content', content);
  formData.append('planIds', planIds);
  formData.append('stylistId', stylistId);
  formData.append('postType', postType || 'work');
  if (serviceTypeId) {
    formData.append('serviceTypeIds', serviceTypeId);
  }

  // Log FormData contents (for debugging)
  console.log('FormData contents:');
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      console.log(`${key}: File(${value.name}, ${value.size} bytes)`);
    } else {
      console.log(`${key}: ${value}`);
    }
  }

  try {
    console.log('Making POST request to /business/showcase');
    const response = await api.post<Showcase>('/business/showcase', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log('Upload successful, response:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error uploading showcase:', error);
    const axiosErr = error as { response?: { data?: unknown } };
    console.error('Error response:', axiosErr.response?.data);
    throw error;
  }
}

export const addShowcaseReply = async ({ showcaseId, content, commentId }: ShowcaseReplyData): Promise<Showcase> => {
  try {
    const response = await api.post<Showcase>(
      `/business/showcase/reply-comment`,
      { showcaseId, content, commentId }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error adding showcase reply:', error);
    throw error;
  }
};

export const updateShowcaseReply = async ({ showcaseId, commentId, replyId, content }: ShowcaseReplyUpdateData): Promise<Showcase> => {
  try {
    const response = await api.put<Showcase>(
      `/business/showcase/reply-comment`,
      { showcaseId, commentId, replyId, content }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating showcase reply:', error);
    throw error;
  }
};

export const deleteShowcaseReply = async ({ showcaseId, commentId, replyId }: ShowcaseReplyDeleteData): Promise<Showcase> => {
  try {
    const response = await api.delete<Showcase>(
      `/business/showcase/reply-comment`,
      { data: { showcaseId, commentId, replyId } }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error deleting showcase reply:', error);
    throw error;
  }
};

/**
 * Bulk update showcases with businessArea for a staff member
 * Used during Instagram onboarding to update all created showcases after profile completion
 */
export const bulkUpdateShowcasesBusinessArea = async (staffId: string, businessArea: string): Promise<{ updatedCount: number }> => {
  try {
    console.log('Bulk updating showcases:', { staffId, businessArea });
    const response = await api.put<{ updatedCount: number }>(
      `/business/showcases/bulk-update-area`,
      { staffId, businessArea }
    );
    console.log('Bulk update response:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error bulk updating showcases:', error);
    throw error;
  }
};
